import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Icon from 'components/Icon';
import { COLORS, FONT, focusRing } from '../lib/tokens';

const Panel = styled.section`
  background: ${COLORS.card};
  border: 1px solid ${COLORS.cardBorder};
  border-radius: 16px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-sizing: border-box;
  font-family: ${FONT};
`;

const Divider = styled.div`
  height: 1px;
  background: ${COLORS.cardBorder};
`;

const Tiles = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
`;

const Tile = styled.div`
  background: ${COLORS.tile};
  border: 1px solid ${COLORS.cardBorder};
  border-radius: 12px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: ${COLORS.text};
  font-size: 12px;
  font-weight: 600;
`;

const Disclosure = styled.p`
  margin: 0;
  text-align: center;
  font-size: 14px;
  line-height: 1.4;
  color: ${COLORS.muted};
`;

const FreeButton = styled.button`
  width: 100%;
  height: 44px;
  border: 1px solid ${COLORS.cardBorder};
  border-radius: 12px;
  background: transparent;
  color: ${COLORS.text};
  font-family: inherit;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  ${focusRing}
`;

const Pills = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;
`;

const pill = `
  flex: 1 1 auto;
  min-width: 0;
  height: 36px;
  padding: 0 14px;
  border: 1px solid ${COLORS.cardBorder};
  border-radius: 999px;
  background: ${COLORS.tile};
  color: ${COLORS.text};
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  cursor: pointer;
`;

const PillButton = styled.button`
  ${pill}
  &:disabled { opacity: 0.6; cursor: default; }
  ${focusRing}
`;

const PillLink = styled(Link)`
  ${pill}
  ${focusRing}
`;

const Message = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 1.35;
  color: ${COLORS.muted};
`;

const PaymentSection = ({ onContinueFree, onRestore, restoring = false, restoreMessage = '' }) => {
  const { t } = useTranslation();
  return (
    <Panel>
      <Divider />
      <Tiles>
        <Tile>
          <Icon type="paywallApple" dimensions={24} col={COLORS.text} />
          {t('subscription.paywall.payment.applePay')}
        </Tile>
        <Tile>
          <Icon type="paywallCircleX" dimensions={24} col={COLORS.text} />
          {t('subscription.paywall.payment.googlePay')}
        </Tile>
        <Tile>
          <Icon type="paywallCreditCard" dimensions={24} col={COLORS.text} />
          {t('subscription.paywall.payment.card')}
        </Tile>
      </Tiles>
      <Disclosure>{t('subscription.paywall.disclosure')}</Disclosure>
      <FreeButton type="button" onClick={onContinueFree}>
        {t('subscription.paywall.continueFree')}
      </FreeButton>
      <Pills>
        <PillButton type="button" onClick={onRestore} disabled={restoring}>
          {restoring ? t('subscription.paywall.restoring') : t('subscription.paywall.restore')}
        </PillButton>
        <PillLink to="/terms-and-conditions">{t('subscription.paywall.terms')}</PillLink>
        <PillLink to="/privacy-policy">{t('subscription.paywall.privacy')}</PillLink>
      </Pills>
      {restoreMessage && <Message role="status">{restoreMessage}</Message>}
    </Panel>
  );
};

export default PaymentSection;
